import { createFocusTrap } from 'focus-trap';

/**
 * Expand icon + download link behaviour for the Image element.
 *
 * Markup contract (see accompanying template snippet):
 * - `.image-figure`            wraps one Image element's markup
 * - `.image-with-fallback`     wraps the (up to two) responsive <img> tags
 * - `.image-expand-btn`        the "expand" trigger (rendered when ShowExpandableIcon)
 * - `.image-download-link`     the download <a> (rendered when ShowDownloadLink)
 * - `figcaption`               the caption, reused verbatim inside the lightbox
 *
 * Each <img> that should be downloadable carries:
 * - `data-download-href`   the URL of the actual downloadable file
 * - `data-download-label`  optional text, e.g. "JPG, 2MB"
 *
 * Import and call `initImageLightbox()` once from your app entry point.
 */

const SELECTORS = {
  // The template has no dedicated wrapper class — <figure> itself is the
  // per-image root, scoped to the element wrapper so we don't pick up
  // unrelated <figure> tags elsewhere on the page.
  figure: '.centreport__elements__image figure',
  wrapper: '.image-with-fallback',
  expandTrigger: '.image-expand-btn',
  downloadLink: '.image-download-link',
  caption: 'figcaption',
};

let lightboxEl = null;
let focusTrap = null;
let lastFocusedEl = null;

/**
 * "Is this element actually rendered on screen?" — checked via layout
 * boxes rather than reading computed `display`, so it doesn't matter
 * *how* the other image is hidden (media query, utility class, JS);
 * this keeps working even if that mechanism changes later.
 */
function isVisible(el) {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function getVisibleImage(figure) {
  const wrapper = figure.querySelector(SELECTORS.wrapper);
  if (!wrapper) return null;
  const images = Array.from(wrapper.querySelectorAll('img'));
  return images.find(isVisible) || images[0] || null;
}

/**
 * Keep the download link pointed at whichever image (desktop or
 * mobile) is currently on screen. Runs on init and on resize, since
 * a viewport change alone (no reload) can flip which image is shown.
 */
function syncDownloadLink(figure) {
  const link = figure.querySelector(SELECTORS.downloadLink);
  if (!link) return;

  const image = getVisibleImage(figure);
  if (!image) return;

  const href = image.dataset.downloadHref;
  if (href) {
    link.setAttribute('href', href);
  }

  const label = image.dataset.downloadLabel;
  const labelTarget = link.querySelector('[data-label-text]');
  if (label && labelTarget) {
    labelTarget.textContent = label;
  }
}

function buildLightbox() {
  const el = document.createElement('div');
  el.className = 'image-lightbox';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', 'Expanded image');

  el.innerHTML = `
    <div class="image-lightbox__dialog">
      <button type="button" class="image-lightbox__close" aria-label="Close">
        <span class="material-symbols-outlined" aria-hidden="true">close</span>
      </button>
      <img class="image-lightbox__img" src="" alt="">
      <div class="image-lightbox__caption"></div>
    </div>
  `;

  document.body.appendChild(el);

  el.querySelector('.image-lightbox__close').addEventListener('click', requestClose);
  el.addEventListener('click', (event) => {
    if (event.target === el) {
      requestClose();
    }
  });

  return el;
}

/**
 * Size .image-lightbox__dialog (and stretch the image to fill it),
 * rather than letting flexbox shrink-to-fit size the dialog — which
 * would use the caption text's *unwrapped* width as a candidate, often
 * wider than the image itself (that's what was pushing the close
 * button away from the image corner).
 *
 * Two passes: first claim the full available width (viewport/1200px)
 * and let the caption wrap at that width; then, if the image at that
 * width plus the wrapped caption would exceed the height budget, back
 * off to whatever width keeps both within the viewport.
 */
function layoutLightboxDialog() {
  if (!lightboxEl) return;

  const dialog = lightboxEl.querySelector('.image-lightbox__dialog');
  const img = lightboxEl.querySelector('.image-lightbox__img');
  const captionEl = lightboxEl.querySelector('.image-lightbox__caption');
  if (!dialog || !img || !captionEl) return;

  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;
  if (!naturalW || !naturalH) return; // not loaded yet

  const maxDialogW = Math.min(window.innerWidth * 0.9, 1200);
  const maxDialogH = window.innerHeight * 0.9;

  // Pass 1: claim the full available width — the image stretches (or
  // scales down) to fill it — and let the caption re-wrap at that width.
  let width = maxDialogW;
  dialog.style.width = `${width}px`;

  // Pass 2: does image + wrapped caption still fit the height budget?
  const captionHeight = captionEl.offsetHeight;
  const imgHeightAtWidth = width * (naturalH / naturalW);

  if (imgHeightAtWidth + captionHeight > maxDialogH) {
    const availableImgHeight = Math.max(maxDialogH - captionHeight, 0);
    const widthByHeight = availableImgHeight * (naturalW / naturalH);
    width = Math.min(width, widthByHeight);
    dialog.style.width = `${width}px`;
  }
}

function performClose() {
  if (!lightboxEl) return;

  lightboxEl.classList.remove('is-open');
  document.body.classList.remove('image-lightbox-open');

  const dialog = lightboxEl.querySelector('.image-lightbox__dialog');
  if (dialog) {
    dialog.style.width = ''; // avoid a stale size flashing before next open's layout pass
  }

  if (lastFocusedEl) {
    lastFocusedEl.focus();
    lastFocusedEl = null;
  }

  focusTrap = null;
}

function requestClose() {
  if (focusTrap) {
    // Triggers the trap's onDeactivate callback, which calls performClose().
    focusTrap.deactivate();
  } else {
    performClose();
  }
}

function openLightbox(trigger) {
  const figure = trigger.closest(SELECTORS.figure);
  const image = figure && getVisibleImage(figure);
  if (!image) return;

  if (!lightboxEl) {
    lightboxEl = buildLightbox();
  }

  const img = lightboxEl.querySelector('.image-lightbox__img');
  img.removeEventListener('load', layoutLightboxDialog);
  img.addEventListener('load', layoutLightboxDialog, { once: true });
  img.src = image.currentSrc || image.src;
  img.alt = image.alt || '';

  const captionEl = lightboxEl.querySelector('.image-lightbox__caption');
  captionEl.innerHTML = '';

  const sourceCaption = figure.querySelector(SELECTORS.caption);
  if (sourceCaption) {
    captionEl.appendChild(sourceCaption.cloneNode(true));
  }

  const sourceDownload = figure.querySelector(SELECTORS.downloadLink);
  if (sourceDownload) {
    captionEl.appendChild(sourceDownload.cloneNode(true));
  }

  lastFocusedEl = trigger;
  lightboxEl.classList.add('is-open');
  document.body.classList.add('image-lightbox-open');

  // If the image is already cached/decoded, 'load' won't fire again — run
  // the layout pass immediately in that case.
  if (img.complete && img.naturalWidth) {
    layoutLightboxDialog();
  }

  focusTrap = createFocusTrap(lightboxEl, {
    initialFocus: '.image-lightbox__close',
    escapeDeactivates: true,
    onDeactivate: performClose,
    allowOutsideClick: true,
  });
  focusTrap.activate();
}

export function ImageLightbox(root = document) {
  const figures = Array.from(root.querySelectorAll(SELECTORS.figure));
  figures.forEach(syncDownloadLink);

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      figures.forEach(syncDownloadLink);
      if (lightboxEl && lightboxEl.classList.contains('is-open')) {
        layoutLightboxDialog();
      }
    }, 150);
  });

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest(SELECTORS.expandTrigger);
    if (trigger) {
      event.preventDefault();
      openLightbox(trigger);
    }
  });
}
